<template>
	<Form as=""
		:validation-schema="formSchema"
		v-slot="{ errors, validate, meta }">

		<v-form
			@submit.prevent="validateForm(validate)"
			data-test="form"
		>

			<!-- FORM INPUT FIELDS -->
			<v-row class="ma-0">

				<!-- Language select -->
				<v-col cols="12"
					class="pa-0 pb-8">
					<scale-dropdown-select
						data-test="locale"
						:value="formFields.locale.value"
						:label="formFields.locale.label"
						@scale-change="formFields.locale.value = $event.target.value">

						<scale-dropdown-select-item value="English">English</scale-dropdown-select-item>
						<scale-dropdown-select-item value="Deutsch">Deutsch</scale-dropdown-select-item>

					</scale-dropdown-select>
				</v-col>

				<!-- First name input -->
				<v-col cols="12"
					class="pa-0 pb-8">
					<scale-text-field
						data-test="first-name"
						:label="formFields.firstName.label"
						:value="formFields.firstName.value"
						@scaleChange="formFields.firstName.value = $event.target.value"
						readonly
						aria-labelledby="body-info"
					></scale-text-field>
				</v-col>

				<!-- Last name input -->
				<v-col cols="12"
					class="pa-0 pb-8">
					<scale-text-field
						data-test="last-name"
						:label="formFields.lastName.label"
						:value="formFields.lastName.value"
						@scaleChange="formFields.lastName.value = $event.target.value"
						readonly
						aria-labelledby="body-info"
					></scale-text-field>
				</v-col>

				<!-- Birth date picker -->
				<v-col cols="12"
					:class="getFieldPadding(errors.hasOwnProperty('birthdate'))">
					<scale-date-picker
						v-model="formFields.birthdate.value"
						data-test="birthdate"
						:label="formFields.birthdate.label"
						@scaleChange="formFields.birthdate.value = $event.detail.value"
						@input="(event) => { event.target.value = event.target.value.replace(/-/g, '') }"
						:helper-text="formFields.birthdate.errorMsg"
						:invalid="formFields.birthdate.errorMsg !== ''"
						:max="maxDate"
						min="1900-01-01"
						aria-labelledby="body-info"
					></scale-date-picker>
				</v-col>

				<!-- Personal/Insurance number radio button  -->
				<v-row class="ma-0 mb-8"
					justify="space-between">
					<v-col cols="12"
						class="pa-0">
						<p class="label"
							id="tpn-rvn-label">{{ t("text.label") }}</p>
						<p class="note bold">{{ t("text.note") }}</p>
					</v-col>
					<scale-radio-button
						name="idSelectRadio"
						value="tpn"
						:label="formFields.tpn.label"
						:checked="choosedIdentifier === 'tpn'"
						@scaleChange="choosedIdentifier = $event.target.value"
						aria-labelledby="tpn-rvn-label"
					></scale-radio-button>
					<scale-radio-button
						name="idSelectRadio"
						value="rvn"
						:label="formFields.rvn.label"
						:checked="choosedIdentifier === 'rvn'"
						@scaleChange="choosedIdentifier = $event.target.value"
						aria-labelledby="tpn-rvn-label"
					></scale-radio-button>
				</v-row>

				<!-- Personal/Insurance number input  -->
				<v-col cols="12"
					class="pa-0">
					<Field
						v-model="formFields[choosedIdentifier].value"
						:name="formFields[choosedIdentifier].name"
						:rules="formSchema[formFields[choosedIdentifier].name]"
						validateOnInput
						v-slot="{ field }"
					>
						<VTextField
							v-bind="field"
							data-test="identifier"
							:label="formFields[choosedIdentifier].label + '*'"
							:placeholder="formFields[choosedIdentifier].placeholder"
							:helper-text="errors[formFields[choosedIdentifier].name]"
							:invalid="errors.hasOwnProperty(`${ formFields[choosedIdentifier].name }`)"
							:max-length="formFields[choosedIdentifier].maxLength"
							:counter="true"
							aria-labelledby="tpn-rvn-label"
						>
							<!-- Info icon inside name text field -->
							<div slot="tooltip-info"
								class="tooltip">
								<scale-tooltip>
									<scale-icon-alert-information
										tabindex="0"
										class="tooltip-icon"
										accessibility-title="information"
										fill="grey"
									/>
									<span slot="content">
										<div>
											<p>
												{{ t("tooltip." + choosedIdentifier) }}
											</p>
											<v-img
												v-if="choosedIdentifier === 'tpn'"
												src="../../public/img/tpn.jpg"
												max-width="350px"
											></v-img>
											<v-img
												v-else
												src="../../public/img/rvn.jpg"
												max-width="350px"
											></v-img>
										</div>
									</span>
								</scale-tooltip>
							</div>
						</VTextField>
					</Field>
				</v-col>
			</v-row>

			<!-- FORM ACTION BUTTONS-->
			<v-row class="ma-0 mt-8 d-flex"
				justify="end">
				<scale-button
					type="submit"
					:disabled="loading || !meta.valid || !isFormValid"
				>
					<!-- Loading spinner -->
					<scale-loading-spinner
						v-if="loading"
						size="small"
					></scale-loading-spinner>
					<span v-else>
						{{ t("buttons.primaryBtn.formScreen") }}
					</span>
				</scale-button>
			</v-row>
		</v-form>
	</Form>
</template>

<script setup>
  import { ref, reactive, computed, defineEmits, onMounted, inject, watch } from 'vue';


  // Components
  import { Form, Field } from 'vee-validate';
  import VTextField from './VTextField.vue';


  // Services
  import RegistrationAPI from '@/services/RegistrationAPI';

  // Plugins
  import { useI18n } from 'vue-i18n';
  const { t } = useI18n();

  // Reusable data
  const keycloak = inject('keycloak');
  const loading = ref(false);
  const isFormValid = ref(true);
  const choosedIdentifier = ref('tpn');
  const formFields = reactive({
    locale: {
      name: 'locale',
      label: t('formFields.locale'),
      value: 'Deutsch',
      errorMsg: '',
    },
    firstName: {
      name: 'firstName',
      label: t('formFields.firstName'),
      value: '',
      errorMsg: '',
    },
    lastName: {
      name: 'lastName',
      label: t('formFields.lastName'),
      value: '',
      errorMsg: '',
    },
    birthdate: {
      name: 'birthdate',
      label: t('formFields.birthdate'),
      value: '',
      errorMsg: '',
      rules: 'required',
    },
    tpn: {
      name: 'tpn',
      label: t('formFields.tpn'),
      value: null,
      maxLength: 10,
      rules: 'required|numeric|length:10',
      placeholder: '0123456789',
    },
    rvn: {
      name: 'rvn',
      label: t('formFields.rvn'),
      value: '',
      maxLength: 12,
      rules: 'required|length:12|rvn',
      placeholder: '12345678A123',
    },
  });


  // Emits
  const emit = defineEmits(['form-submit', 'show-notification']);


  // Fetch data
  onMounted(() => {
    setupDatePicker();
    getUser();
  });

  const getUser = () => {
    loading.value = true;

    RegistrationAPI.getUserName(keycloak.token).then((res) => {
      formFields.firstName.value = res.data?.firstName;
      formFields.lastName.value = res.data?.lastName;
    }).catch((err) => {
      emit("show-notification", 'danger',
           t('errors.userDataError'),
           `${err.response?.data.status || "Unknown status"}: ${err.response?.data.error || "Unknown error"}`);
      console.error('Error getting user -> ', err);
    }).finally(() => {
      loading.value = false;
    });
  };

  // Validation
  const validateForm = (validate) => {
    validate().then((valid) => {
      isFormValid.value = valid;
      if (!formFields.birthdate.value) {
        isFormValid.value = false;
        formFields.birthdate.errorMsg = t('validationMessages.date');
      }
      if (isFormValid.value) {
        if (choosedIdentifier.value === 'tpn') {
          formFields.rvn.value = '';
        } else {
          formFields.tpn.value = '';
        }
        emit('form-submit', formData.value);
      }
    });
  };

  const formSchema = computed(() => {
    const schema = {};

    Object.keys(formFields).forEach(key => {
      const field = formFields[key];
      if ((choosedIdentifier.value === 'tpn' && key !== 'rvn') ||
        (choosedIdentifier.value === 'rvn' && key !== 'tpn')) {
        // Also remove birthdate field from schema to avoid duplication of the error by vee-validate.
        if (field.rules && field.name !== "birthdate") {
          schema[field.name] = field.rules;
        }
      }
    });

    return schema;
  });

  const maxDate = computed(() => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  });


  const formData = computed(() => {
    const bDate = formFields.birthdate.value && formFields.birthdate.value.includes('-')
      ? formFields.birthdate.value.split('-').reverse().join('.')
      : '';

    return {
      locale: formFields.locale.value,
      firstName: formFields.firstName.value,
      lastName: formFields.lastName.value,
      birthdate: bDate,
      tpn: formFields.tpn.value,
      rvn: formFields.rvn.value,
    };
  });

  watch(
    () => formFields.birthdate.value,
    () => {
      isFormValid.value = true;
      formFields.birthdate.errorMsg = "";

      // Scale Design System datepicker doesn't work with vee-validate as expected. This is a custom validation method for datepicker.
      if (!formFields.birthdate.value) {
        isFormValid.value = false;
        formFields.birthdate.errorMsg = t('validationMessages.date');
      }
    },
    { deep: true }
  );


  // Fields settings
  const getFieldPadding = (isFieldInvalid) => {
    return isFieldInvalid ? 'pa-0 pb-3' : 'pa-0 pb-8';
  };

  const setupDatePicker = () => {
    const picker = document.querySelector('scale-date-picker');
    if (picker) {
      picker.localization = {
        buttonLabel: 'Datum wählen',
        placeholder: 'TT.MM.JJJJ',
        selectedDateMessage: 'Gewähltes Datum',
        prevMonthLabel: 'Vorheriger Monat',
        nextMonthLabel: 'Nächster Monat',
        monthSelectLabel: 'Monat',
        yearSelectLabel: 'Jahr',
        closeLabel: 'Fenster schließen',
        keyboardInstruction:
          'Sie können mit den Pfeiltasten vor und zurück navigieren',
        calendarHeading: 'Datum wählen',
        dayNames: [
          'Sonntag',
          'Montag',
          'Dienstag',
          'Mittwoch',
          'Donnerstag',
          'Freitag',
          'Samstag',
        ],
        monthNames: [
          'Januar',
          'Februar',
          'März',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Dezember',
        ],
        monthNamesShort: [
          'Jan',
          'Feb',
          'Mär',
          'Apr',
          'Mai',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Okt',
          'Nov',
          'Dez',
        ],
        today: 'heute',
        locale: 'de-DE',
      };

      const DATE_FORMAT = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

      picker.dateAdapter = {
        parse(value = '', createDate) {
          const matches = value.match(DATE_FORMAT);
          if (matches) {
            return createDate(matches[3], matches[2], matches[1]);
          }
        },
        format(date) {
          return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
        },
      };
    }
  };
</script>

<style scoped>
.tooltip {
  display: inline-block;
  position: relative;
  cursor: pointer;
}
.tooltip-icon {
  margin-left: 12px;
}
</style>
